<div class="employee-profile-container p-6 max-w-6xl mx-auto">
  <!-- Loading State -->
  <div *ngIf="loading()" class="flex justify-center items-center py-12">
    <sl-spinner style="font-size: 2rem;"></sl-spinner>
    <span class="ml-3 text-gray-600">Loading employee details...</span>
  </div>

  <!-- Error State -->
  <div *ngIf="error()" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div class="flex items-center">
      <sl-icon name="exclamation-triangle" class="text-red-500 mr-2"></sl-icon>
      <span class="text-red-700">{{ error() }}</span>
    </div>
  </div>

  <!-- Employee Profile Content -->
  <div *ngIf="!loading() && !error() && employee()" class="space-y-6">
    <!-- Header Section -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div class="flex items-start justify-between">
        <div class="flex items-center space-x-4">
          <app-sl-avatar
            [image]="employee()?.personalDetails.profileImage"
            [label]="getEmployeeInitials()"
            size="x-large"
            shape="circle">
          </app-sl-avatar>
          
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ getEmployeeFullName() }}</h1>
            <p class="text-lg text-gray-600">{{ employee()?.jobDetails.jobTitle }}</p>
            <p class="text-sm text-gray-500">{{ employee()?.jobDetails.department }}</p>
            <div class="flex items-center mt-2 space-x-4">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    [class.bg-green-100]="employee()?.jobDetails.employeeStatus === 'Active'"
                    [class.text-green-800]="employee()?.jobDetails.employeeStatus === 'Active'"
                    [class.bg-yellow-100]="employee()?.jobDetails.employeeStatus === 'Pending'"
                    [class.text-yellow-800]="employee()?.jobDetails.employeeStatus === 'Pending'"
                    [class.bg-red-100]="employee()?.jobDetails.employeeStatus === 'Inactive'"
                    [class.text-red-800]="employee()?.jobDetails.employeeStatus === 'Inactive'">
                {{ employee()?.jobDetails.employeeStatus }}
              </span>
              <span class="text-sm text-gray-500">
                Employee #{{ employee()?.jobDetails.employeeNumber }}
              </span>
            </div>
          </div>
        </div>
        
        <div class="flex space-x-3">
          <app-sl-button
            variant="default"
            prefixIcon="arrow-left"
            (buttonClick)="goBack()">
            Back to List
          </app-sl-button>
          <app-sl-button
            variant="primary"
            prefixIcon="pencil"
            (buttonClick)="editEmployee()">
            Edit Profile
          </app-sl-button>
        </div>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200">
      <div class="border-b border-gray-200">
        <nav class="flex space-x-8 px-6" aria-label="Tabs">
          <button
            (click)="setActiveTab('job')"
            class="py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
            [class.border-blue-500]="activeTab() === 'job'"
            [class.text-blue-600]="activeTab() === 'job'"
            [class.border-transparent]="activeTab() !== 'job'"
            [class.text-gray-500]="activeTab() !== 'job'"
            [class.hover:text-gray-700]="activeTab() !== 'job'">
            <sl-icon name="briefcase" class="mr-2"></sl-icon>
            Job Details
          </button>
          <button
            (click)="setActiveTab('personal')"
            class="py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
            [class.border-blue-500]="activeTab() === 'personal'"
            [class.text-blue-600]="activeTab() === 'personal'"
            [class.border-transparent]="activeTab() !== 'personal'"
            [class.text-gray-500]="activeTab() !== 'personal'"
            [class.hover:text-gray-700]="activeTab() !== 'personal'">
            <sl-icon name="person" class="mr-2"></sl-icon>
            Personal Details
          </button>
          <button
            (click)="setActiveTab('emergency')"
            class="py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200"
            [class.border-blue-500]="activeTab() === 'emergency'"
            [class.text-blue-600]="activeTab() === 'emergency'"
            [class.border-transparent]="activeTab() !== 'emergency'"
            [class.text-gray-500]="activeTab() !== 'emergency'"
            [class.hover:text-gray-700]="activeTab() !== 'emergency'"
            [class.hover:border-gray-300]="activeTab() !== 'emergency'">
            <sl-icon name="phone" class="mr-2"></sl-icon>
            Emergency Contact
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="p-6">
        <!-- Job Details Tab -->
        <div *ngIf="activeTab() === 'job'" class="space-y-6">
          <!-- Basic Job Information -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Job Title</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.jobTitle || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Department</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.department || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Team</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.team || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Location</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.location || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Employment Type</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.employmentType || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Contract Type</label>
              <p class="text-sm text-gray-900">{{ employee()?.jobDetails.contractType || 'Not specified' }}</p>
            </div>
          </div>

          <!-- Employment Dates -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Employment Dates</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Start Date</label>
                <p class="text-sm text-gray-900">{{ formatDate(employee()?.jobDetails.startDate || '') }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">End Date</label>
                <p class="text-sm text-gray-900">{{ formatDate(employee()?.jobDetails.endDate || '') }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Probation End Date</label>
                <p class="text-sm text-gray-900">{{ formatDate(employee()?.jobDetails.probationEndDate || '') }}</p>
              </div>
            </div>
          </div>

          <!-- Reporting Structure -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Reporting Structure</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Manager</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.managerName || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Reports To</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.reportsTo || 'Not specified' }}</p>
              </div>
            </div>
          </div>

          <!-- Work Schedule -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Work Schedule</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Working Hours</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.workingHours || 'Not specified' }} hours/week</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Working Pattern</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.workingPattern || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Working Days</label>
                <p class="text-sm text-gray-900">{{ formatWorkingDays(employee()?.jobDetails.workingDays) }}</p>
              </div>
            </div>
          </div>

          <!-- Compensation -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Compensation</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Salary</label>
                <p class="text-sm text-gray-900">{{ formatSalary(employee()?.jobDetails.salary, employee()?.jobDetails.currency, employee()?.jobDetails.salaryFrequency) }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Cost Center</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.costCenter || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Pension Scheme</label>
                <p class="text-sm text-gray-900">{{ employee()?.jobDetails.pensionScheme || 'Not specified' }}</p>
              </div>
            </div>
          </div>

          <!-- Job Description -->
          <div class="border-t pt-6" *ngIf="employee()?.jobDetails.jobDescription">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Job Description</h3>
            <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ employee()?.jobDetails.jobDescription }}</p>
          </div>
        </div>

        <!-- Personal Details Tab -->
        <div *ngIf="activeTab() === 'personal'" class="space-y-6">
          <!-- Basic Personal Information -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">First Name</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.firstName || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Last Name</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.lastName || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Preferred Name</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.preferredName || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Date of Birth</label>
              <p class="text-sm text-gray-900">{{ formatDate(employee()?.personalDetails.dateOfBirth || '') }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Gender</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.gender || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Nationality</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.nationality || 'Not specified' }}</p>
            </div>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Marital Status</label>
              <p class="text-sm text-gray-900">{{ employee()?.personalDetails.maritalStatus || 'Not specified' }}</p>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Work Email</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.email || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Personal Email</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.personalEmail || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Mobile Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.mobilePhone || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Home Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.homePhone || 'Not specified' }}</p>
              </div>
            </div>
          </div>

          <!-- Address -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Address</h3>
            <div class="space-y-1">
              <label class="text-sm font-medium text-gray-500">Home Address</label>
              <p class="text-sm text-gray-900">{{ formatAddress(employee()?.personalDetails.address) }}</p>
            </div>
          </div>

          <!-- UK Specific Information -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">UK Specific Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">National Insurance Number</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.nationalInsuranceNumber || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Passport Number</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.passportNumber || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Visa Status</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.visaStatus || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Right to Work</label>
                <p class="text-sm text-gray-900">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        [class.bg-green-100]="employee()?.personalDetails.rightToWork"
                        [class.text-green-800]="employee()?.personalDetails.rightToWork"
                        [class.bg-red-100]="!employee()?.personalDetails.rightToWork"
                        [class.text-red-800]="!employee()?.personalDetails.rightToWork">
                    {{ employee()?.personalDetails.rightToWork ? 'Yes' : 'No' }}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <!-- Emergency Contact -->
          <div class="border-t pt-6">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Name</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.emergencyContact?.name || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Relationship</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.emergencyContact?.relationship || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Mobile Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.emergencyContact?.mobilePhone || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Home Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.emergencyContact?.homePhone || 'Not specified' }}</p>
              </div>
              <div class="space-y-1">
                <label class="text-sm font-medium text-gray-500">Email</label>
                <p class="text-sm text-gray-900">{{ employee()?.personalDetails.emergencyContact?.email || 'Not specified' }}</p>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="border-t pt-6" *ngIf="employee()?.personalDetails.notes">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Notes</h3>
            <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ employee()?.personalDetails.notes }}</p>
          </div>
        </div>

        <!-- Emergency Contact Tab -->
        <div *ngIf="activeTab() === 'emergency'" class="space-y-6">
          <!-- Emergency Contact Information -->
          <div>
            <h3 class="text-lg font-medium text-gray-900 mb-4">Emergency Contact Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.name || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.relationship || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Mobile Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.mobilePhone || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Home Phone</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.homePhone || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.email || 'Not provided' }}</p>
              </div>
            </div>
          </div>

          <!-- Emergency Contact Address -->
          <div class="border-t pt-6" *ngIf="employee()?.emergencyContact?.address">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Emergency Contact Address</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.addressLine1 || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.addressLine2 || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.city || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">County</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.county || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.postcode || 'Not provided' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <p class="text-sm text-gray-900">{{ employee()?.emergencyContact?.address?.country || 'Not provided' }}</p>
              </div>
            </div>
          </div>

          <!-- No Emergency Contact Message -->
          <div *ngIf="!employee()?.emergencyContact" class="text-center py-8">
            <sl-icon name="exclamation-triangle" class="text-yellow-500 text-4xl mb-4"></sl-icon>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Emergency Contact Information</h3>
            <p class="text-sm text-gray-500 mb-4">Emergency contact details have not been provided for this employee.</p>
            <app-sl-button
              variant="primary"
              prefixIcon="plus"
              (buttonClick)="editEmployee()">
              Add Emergency Contact
            </app-sl-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
